import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { ArrowRight, Building2, Landmark, Utensils, HeartPulse, Briefcase, Factory } from "lucide-react";
import FundTracker from "../components/FundTracker";
import RollOfHonor from "../components/RollOfHonor";
import BrandStatement from "../components/BrandStatement";
import FeaturedShop from "../components/FeaturedShop";
import MilestoneBanner from "../components/MilestoneBanner";
import CharterMilestone from "../components/CharterMilestone";
import { SpotlightTeaser } from "./Spotlight";

const pillars = [
  { icon: Factory, title: "Manufacturing", desc: "Build production capacity that keeps supply chains within our community." },
  { icon: Utensils, title: "Grocery & Food", desc: "Grocery stores and food franchises that feed and employ our neighborhoods." },
  { icon: Landmark, title: "Community Banks", desc: "Own the vault. Recycle every dollar we deposit back into our own hands." },
  { icon: HeartPulse, title: "Hospitals & Health", desc: "Modern healthcare facilities owned by and for the community." },
  { icon: Building2, title: "Infrastructure", desc: "Real estate, housing, and mixed-use development in the heart of the community." },
  { icon: Briefcase, title: "Jobs & Training", desc: "Every dollar spent creates a job. Every job trained becomes a career." },
];

export default function Landing() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/pledges/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="grain">
      {/* MILESTONE BANNER — Phase I Complete, Phase II Ready */}
      <MilestoneBanner />

      {/* HERO */}
      <section
        className="relative overflow-hidden border-b border-bdu-gold/10"
        data-testid="hero-section"
      >
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: "url(https://images.pexels.com/photos/8276183/pexels-photo-8276183.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-bdu-bg" />

        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 pt-24 pb-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="label-eyebrow" data-testid="hero-eyebrow">A Movement · Est. 2026</div>
            <h1 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] text-bdu-cream tracking-tight" data-testid="hero-headline">
              One Trillion Dollars of Spending Power.
              <span className="block mt-2 text-bdu-gold italic">One Fund. One People.</span>
            </h1>
            <p className="mt-8 text-lg text-bdu-cream/75 max-w-2xl leading-relaxed">
              Black Dollars United is pooling <span className="text-bdu-gold">$50 million over the next two years</span> to build the
              manufacturing plants, grocery stores, banks, hospitals, and food franchises that revitalize our
              communities, create jobs, and keep every dollar circulating where it was earned.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/pledge" className="btn-gold" data-testid="hero-pledge-cta">
                Pledge To The Fund <ArrowRight size={16} />
              </Link>
              <a
                href="https://www.zeffy.com/en-US/ticketing/black-dollars-united-corporations-memberships"
                target="_blank"
                rel="noreferrer"
                data-testid="hero-zeffy-cta"
                className="inline-flex items-center gap-2 px-5 py-3 bg-black text-[#C0C0C0] border border-[#C0C0C0] hover:bg-[#C0C0C0] hover:text-black uppercase tracking-widest text-xs font-semibold transition"
              >
                Donate Fee-Free via Zeffy <ArrowRight size={16} />
              </a>
              <Link to="/directory" className="btn-ghost" data-testid="hero-directory-cta">
                Browse Black-Owned Businesses
              </Link>
            </div>

            {/* Official brand mark */}
            <div className="mt-14 flex items-center gap-5" data-testid="hero-brand-mark">
              <div className="flex-shrink-0 w-24 h-24 sm:w-28 sm:h-28 bg-bdu-cream rounded-full p-3 shadow-[0_20px_60px_-10px_rgba(212,175,55,0.4)] ring-1 ring-bdu-gold/40">
                <img
                  src="https://customer-assets-lqy194kg.emergentagent.net/job_black-business-pool/artifacts/9vsdscm3_BDU_Logo-01.webp"
                  alt="Black Dollars United — Trillion Dollar Spending Power"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <div className="label-eyebrow" style={{ fontSize: "0.55rem" }}>Official Movement Mark</div>
                <div className="mt-1 font-serif text-lg text-bdu-cream">Black Dollars United</div>
                <div className="text-xs text-bdu-cream/50 tracking-wider uppercase">Trillion Dollar Spending Power</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 lg:pl-8 animate-fade-up" style={{ animationDelay: "150ms" }}>
            <FundTracker stats={stats} />
          </div>
        </div>
      </section>

      {/* SPECTACULAR BRAND STATEMENT */}
      <BrandStatement />

      {/* TRILLION DOLLAR SPENDING POWER — VAULT FEATURE */}
      <section
        className="relative overflow-hidden border-y border-bdu-gold/20"
        data-testid="vault-feature-section"
      >
        {/* Backdrop */}
        <div className="absolute inset-0" style={{
          background: "radial-gradient(1200px 500px at 50% 40%, rgba(212,175,55,0.15) 0%, rgba(0,0,0,0) 70%)"
        }} />
        <div className="absolute inset-0" style={{ backgroundColor: "#050505" }} />

        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 py-24 lg:py-32">
          <div className="text-center max-w-4xl mx-auto">
            <div className="label-eyebrow" style={{ color: "rgba(212,175,55,0.9)" }}>
              The Vault Behind The Movement
            </div>
            <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-bdu-cream tracking-tight">
              The <span className="text-bdu-gold italic">Trillion Dollar</span><br />
              Spending Power Fund
            </h2>
            <p className="mt-6 text-bdu-cream/70 text-lg max-w-2xl mx-auto leading-relaxed">
              A community-owned vault. Every deposit compounds Black economic power.
              Every dollar recirculates into the businesses, banks, and infrastructure our neighborhoods have built without.
            </p>
          </div>

          {/* Vault image with dramatic frame */}
          <div className="mt-16 relative mx-auto max-w-4xl group">
            {/* Golden aura */}
            <div className="absolute -inset-6 rounded-full blur-3xl opacity-40 pointer-events-none" style={{
              background: "radial-gradient(circle, rgba(212,175,55,0.5), transparent 70%)"
            }} />
            {/* Frame */}
            <div className="relative border-2 border-bdu-gold/40 p-2 bg-black shadow-[0_40px_80px_-20px_rgba(212,175,55,0.4)] transition-transform duration-700 group-hover:scale-[1.02]">
              <div className="relative overflow-hidden">
                <img
                  src="/brand/tdsp-fund-vault.jpg"
                  alt="Trillion Dollar Spending Power Fund — the vault behind the movement"
                  className="w-full h-auto"
                  data-testid="vault-image"
                />
                {/* Cinematic vignette */}
                <div className="absolute inset-0 pointer-events-none" style={{
                  background: "radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.5) 100%)"
                }} />
              </div>
            </div>
            {/* Corner ornaments */}
            <div className="absolute -top-3 -left-3 w-8 h-8 border-t-2 border-l-2 border-bdu-gold pointer-events-none" />
            <div className="absolute -top-3 -right-3 w-8 h-8 border-t-2 border-r-2 border-bdu-gold pointer-events-none" />
            <div className="absolute -bottom-3 -left-3 w-8 h-8 border-b-2 border-l-2 border-bdu-gold pointer-events-none" />
            <div className="absolute -bottom-3 -right-3 w-8 h-8 border-b-2 border-r-2 border-bdu-gold pointer-events-none" />
          </div>

          {/* Dual donate CTAs */}
          <div className="mt-16 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/pledge"
              data-testid="vault-stripe-cta"
              className="btn-gold text-base px-8 py-4"
            >
              Donate with Card <ArrowRight size={18} />
            </Link>
            <a
              href="https://www.zeffy.com/en-US/ticketing/black-dollars-united-corporations-memberships"
              target="_blank"
              rel="noreferrer"
              data-testid="vault-zeffy-cta"
              className="inline-flex items-center gap-2 px-8 py-4 uppercase tracking-widest text-xs font-bold text-black transition-all hover:brightness-110 shadow-[0_10px_30px_-5px_rgba(192,192,192,0.5)]"
              style={{
                background: "linear-gradient(135deg, #E8E8E8 0%, #C0C0C0 50%, #A8A8A8 100%)",
                border: "1px solid #B0B0B0",
              }}
            >
              Donate Fee-Free via Zeffy <ArrowRight size={18} />
            </a>
          </div>
          <p className="mt-6 text-center text-xs text-bdu-cream/50 uppercase tracking-widest">
            All funds → Citizen Trust Bank · Trillion Dollar Spending Power Fund
          </p>
        </div>
      </section>

      {/* CHARTER MILESTONE — compact live progress card */}
      <CharterMilestone compact />

      {/* MISSION STATEMENT */}
      <section className="max-w-6xl mx-auto px-6 sm:px-10 py-28 border-b border-bdu-gold/10" data-testid="mission-section">
        <div className="label-eyebrow">The Mission</div>
        <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight text-bdu-cream max-w-4xl">
          We raise <span className="text-bdu-gold">$50 million in 24 months</span>. We build the businesses
          our communities cannot live without. We hire our own. We own the outcome.
        </h2>
        <div className="mt-10 divider-gold" />
        <p className="mt-10 text-lg text-bdu-cream/75 leading-loose max-w-3xl">
          For every dollar that leaves our community and never returns, another door closes.
          Black Dollars United reverses that flow. Every contributor becomes a stakeholder in the
          <span className="text-bdu-gold"> Trillion Dollar Spending Power Fund</span> — a permanent
          community-owned engine that finances the infrastructure, jobs, and institutions that our
          people have too often had to build without capital.
        </p>
      </section>

      {/* WHAT WE BUILD */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-28" data-testid="pillars-section">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div>
            <div className="label-eyebrow">Where the Fund Goes</div>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-bdu-cream">Six Pillars of Ownership</h2>
          </div>
          <p className="max-w-md text-bdu-cream/60 text-sm">
            The $50M is not charity. It is capital. Deployed across six categories designed to
            create durable, revenue-generating community assets.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pillars.map((p, i) => (
            <div key={p.title} className="bdu-card animate-fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`pillar-${p.title.toLowerCase().split(" ")[0]}`}>
              <p.icon size={28} strokeWidth={1.25} className="text-bdu-gold" />
              <h3 className="mt-6 font-serif text-2xl text-bdu-cream">{p.title}</h3>
              <p className="mt-3 text-sm text-bdu-cream/60 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SHOP — Wear the movement */}
      <FeaturedShop />

      {/* SPOTLIGHT — Faces of Black Excellence */}
      <SpotlightTeaser />

      {/* ROLL OF HONOR */}
      <RollOfHonor />

      {/* CTA STRIP */}
      <section className="relative border-t border-bdu-gold/10 py-24" data-testid="cta-section">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage: "url(https://images.pexels.com/photos/34243918/pexels-photo-34243918.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-bdu-bg via-black/70 to-bdu-bg" />
        <div className="relative max-w-5xl mx-auto px-6 sm:px-10 text-center">
          <div className="label-eyebrow">Two Years. Fifty Million. One Fund.</div>
          <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl text-bdu-cream leading-tight">
            The next generation of Black wealth <span className="italic text-bdu-gold">starts with your dollar</span>.
          </h2>
          <div className="mt-10 flex justify-center gap-4 flex-wrap">
            <Link to="/register" className="btn-gold" data-testid="cta-join">Become a Founding Member</Link>
            <Link to="/pledge" className="btn-ghost" data-testid="cta-pledge">Make Your Pledge</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
